"use client";
import React from "react";
import { motion } from "motion/react";
import {
  Leaf,
  Recycle,
  TreePine,
  Wind,
  Droplet,
  Sun,
  CheckCircle,
} from "lucide-react";
import { useTranslation } from "react-i18next";
import { ImageWithFallback } from "@/src/components/figma/ImageWithFallback";
import { GlassCard } from "@/src/components/GlassCard";
import { AnimatedCounter } from "@/src/components/AnimatedCounter";

export const SustainabilityPage: React.FC = () => {
  const { t } = useTranslation("common");

  const initiatives = [
    {
      icon: Recycle,
      title: t("sustainability.initiatives.recyclable_materials.title"),
      description: t(
        "sustainability.initiatives.recyclable_materials.description",
      ),
    },
    {
      icon: TreePine,
      title: t("sustainability.initiatives.fsc_certified.title"),
      description: t("sustainability.initiatives.fsc_certified.description"),
    },
    {
      icon: Wind,
      title: t("sustainability.initiatives.carbon_neutral.title"),
      description: t("sustainability.initiatives.carbon_neutral.description"),
    },
    {
      icon: Droplet,
      title: t("sustainability.initiatives.water_conservation.title"),
      description: t(
        "sustainability.initiatives.water_conservation.description",
      ),
    },
    {
      icon: Sun,
      title: t("sustainability.initiatives.renewable_energy.title"),
      description: t("sustainability.initiatives.renewable_energy.description"),
    },
    {
      icon: Leaf,
      title: t("sustainability.initiatives.biodegradable_options.title"),
      description: t(
        "sustainability.initiatives.biodegradable_options.description",
      ),
    },
  ];

  const stats = [
    {
      value: 100,
      suffix: "%",
      label: t("sustainability.stats.recyclable_materials"),
    },
    {
      value: 70,
      suffix: "%",
      label: t("sustainability.stats.water_reduction"),
    },
    {
      value: 60,
      suffix: "%",
      label: t("sustainability.stats.renewable_energy"),
    },
    {
      value: 0,
      suffix: "",
      label: t("sustainability.stats.landfill_waste_target"),
    },
  ];

  return (
    <div className="overflow-hidden">
      {/* Hero Section */}
      <section className="relative min-h-[70vh] flex items-center justify-center px-4 sm:px-6 lg:px-8 pt-24">
        <div className="absolute inset-0 z-0">
          <ImageWithFallback
            src="https://images.unsplash.com/photo-1755606396356-bdd7cd95df75?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGVjbyUyMHBhY2thZ2luZyUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080"
            alt="Sustainability"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-br from-green-900/90 via-walnut-800/80 to-green-700/70 dark:from-black/90 dark:via-green-900/85 dark:to-walnut-800/75" />
        </div>

        <div className="relative z-10 max-w-7xl mx-auto text-center py-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8 }}
          >
            <h1 className="text-5xl md:text-7xl font-bold text-white mb-6">
              {t("sustainability.hero.title")}
            </h1>
            <p className="text-xl text-walnut-100 max-w-3xl mx-auto">
              {t("sustainability.hero.subtitle")}
            </p>
          </motion.div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats.map((stat, index) => (
              <GlassCard key={index} className="p-6 text-center">
                <div className="text-4xl md:text-5xl font-bold text-green-700 dark:text-green-400 mb-2">
                  <AnimatedCounter end={stat.value} suffix={stat.suffix} />
                </div>
                <div className="text-sm text-walnut-600 dark:text-walnut-400">
                  {stat.label}
                </div>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Initiatives */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-4xl md:text-5xl font-bold text-walnut-900 dark:text-white mb-4">
              {t("sustainability.initiatives.sectionTitle")}
            </h2>
            <p className="text-lg text-walnut-700 dark:text-walnut-300 max-w-2xl mx-auto">
              {t("sustainability.initiatives.sectionSubtitle")}
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {initiatives.map((initiative, index) => (
              <GlassCard key={index} className="p-6">
                <div className="w-12 h-12 rounded-lg bg-green-100 dark:bg-green-900 flex items-center justify-center mb-4">
                  <initiative.icon className="w-6 h-6 text-green-700 dark:text-green-400" />
                </div>
                <h3 className="text-xl font-semibold text-walnut-900 dark:text-white mb-3">
                  {initiative.title}
                </h3>
                <p className="text-walnut-700 dark:text-walnut-300">
                  {initiative.description}
                </p>
              </GlassCard>
            ))}
          </div>
        </div>
      </section>

      {/* Environmental Commitment */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-2 gap-12">
            <GlassCard className="p-8">
              <Leaf className="w-12 h-12 text-green-700 dark:text-green-400 mb-6" />
              <h2 className="text-3xl font-bold text-walnut-900 dark:text-white mb-6">
                {t("sustainability.environmentalCommitment.title")}
              </h2>
              <p className="text-lg text-walnut-700 dark:text-walnut-300 mb-4">
                {t("sustainability.environmentalCommitment.paragraph1")}
              </p>
              <p className="text-lg text-walnut-700 dark:text-walnut-300">
                {t("sustainability.environmentalCommitment.paragraph2")}
              </p>
            </GlassCard>

            <GlassCard className="p-0 overflow-hidden">
              <ImageWithFallback
                src="https://images.unsplash.com/photo-1755606396356-bdd7cd95df75?crop=entropy&cs=tinysrgb&fit=max&fm=jpg&ixid=M3w3Nzg4Nzd8MHwxfHNlYXJjaHwxfHxzdXN0YWluYWJsZSUyMGVjbyUyMHBhY2thZ2luZyUyMG1hdGVyaWFsc3xlbnwxfHx8fDE3NzE2MDc5NDV8MA&ixlib=rb-4.1.0&q=80&w=1080"
                alt="Sustainable materials"
                className="w-full h-full object-cover"
              />
            </GlassCard>
          </div>
        </div>
      </section>

      {/* Goals */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-7xl mx-auto">
          <GlassCard className="p-12">
            <h2 className="text-4xl font-bold text-walnut-900 dark:text-white mb-8 text-center">
              {t("sustainability.goals.title")}
            </h2>
            <div className="grid md:grid-cols-2 gap-8">
              <div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-walnut-700 dark:text-walnut-300">
                      {t("sustainability.goals.renewableEnergy")}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-walnut-700 dark:text-walnut-300">
                      {t("sustainability.goals.eliminatePlastics")}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-walnut-700 dark:text-walnut-300">
                      {t("sustainability.goals.waterReduction")}
                    </span>
                  </li>
                </ul>
              </div>
              <div>
                <ul className="space-y-4">
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-walnut-700 dark:text-walnut-300">
                      {t("sustainability.goals.zeroWaste")}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-walnut-700 dark:text-walnut-300">
                      {t("sustainability.goals.partnerships")}
                    </span>
                  </li>
                  <li className="flex items-start gap-3">
                    <CheckCircle className="w-6 h-6 text-green-600 dark:text-green-400 flex-shrink-0 mt-0.5" />
                    <span className="text-lg text-walnut-700 dark:text-walnut-300">
                      {t("sustainability.goals.reforestation")}
                    </span>
                  </li>
                </ul>
              </div>
            </div>
          </GlassCard>
        </div>
      </section>
    </div>
  );
};
